import React, { useEffect, useState } from "react"
import { Carsharing, Ridecell } from "@ridecell/web-sdk"
import {
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Box,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
  Snackbar,
} from "@material-ui/core"
import Alert from "@material-ui/lab/Alert"
import moment from "moment"
import { injectIntl } from "react-intl"
import ArrowForwardIcon from "@material-ui/icons/ArrowForward"
import { TextInput } from "@ridecell/web-components-material"

import Carousel from "../VehicleCarousel"
import LoadingButton from "../LoadingButton"
import useGlobalContext from "../../utils/customHooks/useGlobalContext"
import PopupModal from "../../utils/elements/PopupModal"
import messages from "./messages"
import policyKeyMessages from "../WebAppPortal/AccountCreation/PolicyKeyMessages"
import ConsentOptions from "../WebAppPortal/AccountCreation/ConsentOptions"

const CreateRental = ({
  activeProfile,
  customer,
  intl: { formatMessage },
  login,
  markets,
  selectedStation: mapStation,
  rentalMode,
  setSelectedMarket,
  selectedMarket,
  setMapCenter,
  stations,
  userLoggedIn,
}) => {
  const [snackBar, setSnackBar] = useState(false)
  const [consentOptions, setConsentOptions] = useState([])
  const [policyDocs, setPolicyDocs] = useState([])
  const [standAlonePolicyDocsLen, setStandalonePolicyDocsLen] = useState(0)
  const [standaloneTermsAccepted, setStandaloneTermsAccepted] = useState([])
  const [nonStandaloneTermsAccepted, setNonStandaloneTermsAccepted] = useState(
    false
  )
  const [step, setStep] = useState(0)
  const [selectedVehicleType, setSelectedVehicleType] = useState(null)
  const [vehicleTypes, setVehicleTypes] = useState(null)
  const [startDates, setStartDates] = useState([])
  const [startTimes, setStartTimes] = useState([])
  const [endDates, setEndDates] = useState([])
  const [endTimes, setEndTimes] = useState([])
  const [loading, setLoading] = useState(false)
  const [reserveLoading, setReserveLoading] = useState(false)
  const [selectedStation, setSelectedStation] = useState("")
  const [selectedStartDate, setSelectedStartDate] = useState("")
  const [selectedStartTime, setSelectedStartTime] = useState("")
  const [selectedEndDate, setSelectedEndDate] = useState("")
  const [selectedEndTime, setSelectedEndTime] = useState("")
  const [srCreated, setSRCreated] = useState(null)
  const [srFailed, setSRFailed] = useState(null)
  const [rentalReason, setRentalReason] = useState(null)
  const singleMarket = markets.length === 1
  const validLogin = userLoggedIn && customer

  const {
    state: {
      config,
      consentDecisions,
      consentAcceptedOptions,
      tenantId,
      tenantSettings,
      error,
    },
    setError,
    setConsentAcceptedOptions,
  } = useGlobalContext()
  const toggleSnackBar = () => setSnackBar(!snackBar)
  const isBusinessRental = activeProfile && activeProfile.business_info
  const rentalValueRequiredAndNotExists = isBusinessRental
    ? !rentalReason
    : false

  // Auto select market when we only have 1
  useEffect(() => {
    if (markets && singleMarket) {
      setSelectedMarket(markets[0])
    }
  }, [markets, setSelectedMarket, singleMarket])

  useEffect(() => {
    ;(async () => {
      try {
        let resp = await Ridecell.getConsentOptions({
          user_id: customer?.uuid,
          market_id: selectedMarket?.id,
          page: 1,
          page_size: 25,
          tenant_id: tenantId,
        })
        setConsentOptions(resp.results)
        if (customer){
        resp = await Ridecell.getActionRequiredPolicy({
          user_id: customer?.uuid,
          page: 1,
          page_size: 25,
          tenant_id: tenantId,
        })}
        if (resp.policy_documents) setPolicyDocs(resp.policy_documents)
      } catch (e) {
        setError(e)
      }
    })()
  }, [selectedEndTime, customer])

  useEffect(() => {
    const selectedConsentOptions = []
    consentOptions.forEach((option) => {
      if (option.checkbox_user) selectedConsentOptions.push(option.id)
    })
    setConsentAcceptedOptions(selectedConsentOptions)
  }, [consentOptions])

  useEffect(() => {
    const countStandAlonePolicyDocs =
      (policyDocs && policyDocs.filter((doc) => doc.is_standalone)) || []
    const standAlonePolicyDocsLength =
      (countStandAlonePolicyDocs && countStandAlonePolicyDocs.length) || 0
    setStandalonePolicyDocsLen(standAlonePolicyDocsLength)
  }, [policyDocs])

  useEffect(() => {
    if (!mapStation) {
      return
    } else {
      setSelectedStation(mapStation)
    }
  }, [mapStation])

  // Fetch Start Dates
  useEffect(() => {
    if (selectedStation === "") return
    Carsharing.getAvailableStartDates({
      station_id: selectedStation.id,
      ...rentalMode,
    })
      .then((startDates) => {
        setStartDates(startDates)
      })
      .catch((error) => {
        setError(error)
      })
  }, [rentalMode, selectedStation, selectedMarket])

  // Blank out values when station or start date changes
  useEffect(() => {
    setStartTimes([])
    setSelectedStartTime("")
    setEndDates([])
    setSelectedEndDate("")
    setEndTimes([])
    setSelectedEndTime("")
  }, [selectedStation, selectedStartDate])

  // Fetch Start Times
  useEffect(() => {
    if (selectedStation === "" || selectedStartDate === "") {
      return
    }
    Carsharing.getAvailableStartTimes({
      station_id: selectedStation.id,
      date: selectedStartDate,
      ...rentalMode,
    })
      .then((startTimes) => {
        setStartTimes(startTimes)
      })
      .catch((error) => {
        setError(error)
      })
  }, [rentalMode, selectedStartDate, selectedStation])

  // Fetch End Dates
  useEffect(() => {
    if (selectedStation === "" || selectedStartTime === "") {
      return
    }
    Carsharing.getAvailableEndDates({
      station_id: selectedStation.id,
      start_date_time: selectedStartTime,
      ...rentalMode,
    })
      .then((endDates) => {
        setEndDates(endDates)
      })
      .catch((error) => {
        setError(error)
      })
  }, [rentalMode, selectedStation, selectedStartTime])

  // Fetch End Times
  useEffect(() => {
    if (selectedStation === "" || selectedEndDate === "") {
      return
    }
    Carsharing.getAvailableEndTimes({
      station_id: selectedStation.id,
      start_date_time: selectedStartTime,
      date: selectedEndDate,
      ...rentalMode,
    })
      .then((endTimes) => {
        setEndTimes(endTimes) // we are surely living in the endTimes
      })
      .catch((error) => {
        setError(error)
      })
  }, [rentalMode, selectedEndDate, selectedStation, selectedStartTime])

  const consentOptionsNeeded =
    consentOptions.length > 0 &&
    config?.should_agree_and_skip_consent_options === false

  const setDataEmpty = () => {
    setSelectedStation("")
    setSelectedMarket("")
    setSelectedVehicleType(null)
    setVehicleTypes(null)
    setStartTimes([])
    setSelectedStartDate("")
    setSelectedStartTime("")
    setEndDates([])
    setSelectedEndDate("")
    setEndTimes([])
    setSelectedEndTime("")
  }

  const goToNextStep = () => {
    if (consentOptionsNeeded) {
      setStep(1)
    } else createScheduledRental()
  }

  const handleChange = (func) => ({ target: { value } }) => {
    if (func === setSelectedMarket && value.display_location) {
      setMapCenter({
        lat: value.display_location.latitude,
        lng: value.display_location.longitude,
      })
    }
    func(value)
    setVehicleTypes(null)
    setSelectedVehicleType(null)
  }

  const findVehicles = () => {
    setLoading(true)
    Carsharing.getAvailableVehicleTypes({
      page: 1,
      page_size: 100,
      station_id: selectedStation.id,
      start_date_time: selectedStartTime,
      end_date_time: selectedEndTime,
      ...rentalMode,
    })
      .then((vehicleTypes) => {
        setVehicleTypes(vehicleTypes)
        setLoading(false)
      })
      .catch((error) => {
        setError(error)
        setLoading(false)
      })
  }

  const selectVehicleType = (vehicleType) => setSelectedVehicleType(vehicleType)

  const consentData =
    consentDecisions &&
    consentDecisions.map((option) => {
      return {
        consent_option: option.id,
        option_version: option.option_version,
        user_decision: consentAcceptedOptions.includes(option.id),
      }
    })

  const policyData =
    policyDocs &&
    policyDocs.map((policy) => {
      return {
        document_version: policy.document_version,
        policy_document: policy.id,
      }
    })

  const createScheduledRental = () => {
    Carsharing.scheduleRental({
      start_station_id: selectedStation.id,
      start_date_time: selectedStartTime,
      end_date_time: selectedEndTime,
      vehicle_type_id: selectedVehicleType.id,
      service_id: selectedVehicleType.service_id,
      ...(isBusinessRental && rentalReason && { rental_reason: rentalReason }),
      ...rentalMode,
    })
      .then((scheduledRental) => {
        setStep(0)
        setSnackBar(true)
        setDataEmpty()
        setSRCreated(scheduledRental)
        setReserveLoading(false)
        setTimeout(() => setSRCreated(null), 5000)
      })
      .catch((error) => {
        setError(error)
        setSRFailed(error.user_message)
        setReserveLoading(false)
        setTimeout(() => setSRFailed(null), 5000)
      })
  }

  const createConsent = async () => {
    if (consentOptionsNeeded) {
      await Ridecell.createConsent({
        data: consentData,
        queryParams: { user_id: customer.uuid, tenant_id: tenantId },
      })
        .then(() => {
          createScheduledRental()
        })
        .catch((error) => {
          setError(error)
          setReserveLoading(false)
        })
    } else {
      createScheduledRental()
    }
  }

  const onReserveVehicle = async () => {
    if (!selectedVehicleType) return
    setReserveLoading(true)

    if (policyDocs.length > 0) {
      await Ridecell.createPolicy({
        data: policyData,
        queryParams: { user_id: customer.uuid, tenant_id: tenantId },
      })
        .then(() => {
          setPolicyDocs([])
          createConsent()
        })
        .catch((error) => {
          setError(error)
          setReserveLoading(false)
        })
    } else {
      createConsent()
    }
  }

  const formatTime = (time) => {
    return moment(time).format(tenantSettings.use24hrClock ? "HH:mm" : "LT")
  }

  const onCheckBoxChange = (e, index) => {
    e.target.checked
      ? setStandaloneTermsAccepted([...standaloneTermsAccepted, index])
      : setStandaloneTermsAccepted([
          ...standaloneTermsAccepted.filter((idx) => idx !== index),
        ])
  }

  const nonStandalonePolicyDocsLen =
    policyDocs && policyDocs.length - standAlonePolicyDocsLen

  const isNextEnabled =
    standAlonePolicyDocsLen === standaloneTermsAccepted.length &&
    (nonStandaloneTermsAccepted || nonStandalonePolicyDocsLen === 0) &&
    selectedVehicleType

  const showCreateScheduledRentalButton =
    !policyDocs || !consentOptionsNeeded || step === 1

  return (
    <Grid container direction="row" spacing={2}>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={formatMessage(messages.scheduleARental)}
            titleTypographyProps={{ variant: "h6" }}
          />
          <CardContent>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  {!singleMarket && (
                    <Grid item xs={6}>
                      <InputLabel>{formatMessage(messages.markets)}</InputLabel>
                      <Select
                        disabled={markets.length === 0}
                        fullWidth
                        onChange={handleChange(setSelectedMarket)}
                        value={selectedMarket}
                      >
                        <MenuItem value="">
                          <em>{formatMessage(messages.none)}</em>
                        </MenuItem>
                        {markets &&
                          markets.map((market) => (
                            <MenuItem key={market.id} value={market}>
                              {market.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </Grid>
                  )}
                  <Grid item xs={singleMarket ? 12 : 6}>
                    <InputLabel>{formatMessage(messages.station)}</InputLabel>
                    <Select
                      disabled={stations.length === 0}
                      fullWidth
                      onChange={handleChange(setSelectedStation)}
                      value={selectedStation}
                    >
                      <MenuItem value="">
                        <em>{formatMessage(messages.none)}</em>
                      </MenuItem>
                      {stations &&
                        stations.map((station) => (
                          <MenuItem key={station.id} value={station}>
                            {station.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={6}>
                    <InputLabel>{formatMessage(messages.startDate)}</InputLabel>
                    <Select
                      disabled={!selectedStation}
                      fullWidth
                      value={selectedStartDate}
                      onChange={handleChange(setSelectedStartDate)}
                    >
                      <MenuItem value="">
                        <em>{formatMessage(messages.none)}</em>
                      </MenuItem>
                      {startDates &&
                        startDates.map((item) => (
                          <MenuItem key={item} value={item}>
                            {moment(item).format("ll")}
                          </MenuItem>
                        ))}
                    </Select>
                  </Grid>
                  <Grid item xs={6}>
                    <InputLabel>{formatMessage(messages.startTime)}</InputLabel>
                    <Select
                      disabled={!selectedStartDate}
                      fullWidth
                      onChange={handleChange(setSelectedStartTime)}
                      value={selectedStartTime}
                    >
                      <MenuItem value="">
                        <em>{formatMessage(messages.none)}</em>
                      </MenuItem>
                      {startTimes &&
                        startTimes.map((item) => (
                          <MenuItem value={item}>{formatTime(item)}</MenuItem>
                        ))}
                    </Select>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={6}>
                    <InputLabel>{formatMessage(messages.endDate)}</InputLabel>
                    <Select
                      disabled={endDates.length === 0}
                      fullWidth
                      onChange={handleChange(setSelectedEndDate)}
                      value={selectedEndDate}
                    >
                      <MenuItem value="">
                        <em>{formatMessage(messages.none)}</em>
                      </MenuItem>
                      {endDates &&
                        endDates.map((item) => (
                          <MenuItem value={item}>
                            {moment(item).format("ll")}
                          </MenuItem>
                        ))}
                    </Select>
                  </Grid>
                  <Grid item xs={6}>
                    <InputLabel>{formatMessage(messages.endTime)}</InputLabel>
                    <Select
                      disabled={endTimes.length === 0}
                      fullWidth
                      onChange={handleChange(setSelectedEndTime)}
                      value={selectedEndTime}
                    >
                      <MenuItem value="">
                        <em>{formatMessage(messages.none)}</em>
                      </MenuItem>
                      {endTimes &&
                        endTimes.map((item) => (
                          <MenuItem value={item}>{formatTime(item)}</MenuItem>
                        ))}
                    </Select>
                  </Grid>
                  {isBusinessRental && (
                    <Grid item xs={6}>
                      <InputLabel>
                        {formatMessage(messages.rentalReason)}
                      </InputLabel>
                      <TextInput
                        color="secondary"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        name="rental_reason"
                        onChange={(e) => setRentalReason(e.target.value)}
                        value={rentalReason}
                        required
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item>
                <LoadingButton
                  dataTestId="findVehiclesButton"
                  disabled={
                    validLogin
                      ? !selectedStation ||
                        !selectedStartTime ||
                        !selectedEndTime ||
                        rentalValueRequiredAndNotExists
                      : false
                  }
                  onClick={validLogin ? findVehicles : login}
                  text={
                    validLogin
                      ? formatMessage(messages.findAvailableVehicles)
                      : formatMessage(messages.loginToReserve)
                  }
                  loading={loading}
                  props={{ fullWidth: true }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      {vehicleTypes && (
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={formatMessage(messages.results)}
              titleTypographyProps={{ variant: "h6" }}
            />
            <CardContent>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12}>
                  <Carousel
                    vehicleTypes={vehicleTypes}
                    selectVehicleType={selectVehicleType}
                    selectedVehicleType={selectedVehicleType}
                  />
                  <Grid item x={12}>
                    {step === 0 && vehicleTypes.length > 0 && (
                      <React.Fragment>
                        {standAlonePolicyDocsLen > 0 &&
                          policyDocs.map((doc, index) => {
                            const messageKey = doc.policy_checkbox_text_key
                            return doc.is_standalone ? (
                              <Box display="flex">
                                <Checkbox
                                  size="medium"
                                  checked={standaloneTermsAccepted.includes(
                                    index
                                  )}
                                  onChange={(e) => {
                                    onCheckBoxChange(e, index)
                                  }}
                                />
                                <Typography
                                  style={{
                                    paddingTop: "9px",
                                  }}
                                >
                                  {formatMessage(policyKeyMessages(messageKey))}
                                  <Link
                                    underline="always"
                                    target="_blank"
                                    href={doc.document_url}
                                  >
                                    {doc.title}
                                  </Link>
                                </Typography>
                              </Box>
                            ) : null
                          })}
                        {nonStandalonePolicyDocsLen > 0 && (
                          <Box
                            display="flex"
                            flexDirection="row"
                            sx={{ mb: "16px" }}
                          >
                            <Checkbox
                              size="medium"
                              checked={nonStandaloneTermsAccepted}
                              onChange={(e) => {
                                setNonStandaloneTermsAccepted(e.target.checked)
                              }}
                            />

                            <Box
                              display="flex"
                              justifyContent="center"
                              flexDirection="column"
                              alignContent="center"
                            >
                              <Typography>
                                {formatMessage(messages.iHaveReadAndAcceptThe)}
                                {policyDocs.map((doc, index) => {
                                  return !doc.is_standalone ? (
                                    <Link
                                      underline="always"
                                      target="_blank"
                                      href={doc.document_url}
                                    >
                                      {doc.title}
                                      {policyDocs.length - 1 !== index && ","}
                                    </Link>
                                  ) : null
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </React.Fragment>
                    )}
                  </Grid>
                </Grid>
                <Grid>
                  {step === 1 && vehicleTypes.length > 0 && (
                    <ConsentOptions
                      options={consentOptions}
                      handleNext={createScheduledRental}
                      showNextButton={false}
                    />
                  )}
                </Grid>
                {vehicleTypes.length > 0 && (
                  <Grid item xs={12}>
                    {showCreateScheduledRentalButton ? (
                      <LoadingButton
                        disabled={!isNextEnabled}
                        onClick={onReserveVehicle}
                        text={formatMessage(messages.reserveVehicle)}
                        loading={reserveLoading}
                        props={{ fullWidth: true }}
                      />
                    ) : (
                      <LoadingButton
                        disabled={!isNextEnabled}
                        text={formatMessage(messages.next)}
                        variant="contained"
                        loading={loading}
                        fullWidth
                        onClick={goToNextStep}
                        props={{
                          fullWidth: true,
                          endIcon: <ArrowForwardIcon />,
                          size: "large",
                        }}
                      />
                    )}
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        autoHideDuration={5000}
        onClose={toggleSnackBar}
        open={snackBar}
      >
        <Alert
          onClose={toggleSnackBar}
          severity={srFailed ? "error" : "success"}
          variant="filled"
        >
          {srFailed
            ? srFailed
            : formatMessage(messages.rentalSuccessfullyScheduled)}
        </Alert>
      </Snackbar>
      <PopupModal
        hasError={error.hasError}
        errorMessage={error.errorMessage}
        onClose={() => setError()}
      />
    </Grid>
  )
}

export default injectIntl(CreateRental)
